import { useState, useEffect } from 'react';
import { api } from '../../core/apiClient';

interface CashFlowProjection {
  month: number;
  year: number;
  opening_balance: number;
  total_incoming: number;
  total_outgoing: number;
  closing_balance: number;
}

const MONTH_NAMES = [
  'Gener', 'Febrer', 'Març', 'Abril', 'Maig', 'Juny',
  'Juliol', 'Agost', 'Setembre', 'Octubre', 'Novembre', 'Desembre',
];

export function CashFlowManagement() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [projections, setProjections] = useState<CashFlowProjection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjections();
  }, [year]);

  const loadProjections = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await api.get(`/comptabilitat/cashflow/yearly/${year}`);
      if (response.success) {
        setProjections(response.data || []);
      }
    } catch (err: any) {
      setError(err?.message || 'Error en carregar les projeccions');
    } finally {
      setLoading(false);
    }
  };

  const totals = projections.reduce(
    (acc, p) => ({
      incoming: acc.incoming + Number(p.total_incoming || 0),
      outgoing: acc.outgoing + Number(p.total_outgoing || 0),
      net: acc.net + (Number(p.total_incoming || 0) - Number(p.total_outgoing || 0)),
    }),
    { incoming: 0, outgoing: 0, net: 0 }
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Tresoreria - Cash Flow</h1>
        <p className="text-gray-600">Projeccions de flux de caixa anuals</p>
      </div>

      <div className="flex items-center gap-4">
        <label className="font-bold">Any:</label>
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          className="px-3 py-2 border rounded w-28"
        />
        <button
          onClick={loadProjections}
          disabled={loading}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
        >
          {loading ? 'Carregant...' : 'Actualitzar'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700">{error}</div>
      )}

      {projections.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded border border-green-200">
            <div className="text-sm text-gray-600">Total Ingressos</div>
            <div className="text-2xl font-bold text-green-600">
              {totals.incoming.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
            </div>
          </div>
          <div className="bg-red-50 p-4 rounded border border-red-200">
            <div className="text-sm text-gray-600">Total Sortides</div>
            <div className="text-2xl font-bold text-red-600">
              {totals.outgoing.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
            </div>
          </div>
          <div className={`p-4 rounded border ${totals.net >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'}`}>
            <div className="text-sm text-gray-600">Net Anual</div>
            <div className={`text-2xl font-bold ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
              {totals.net.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded border">
        <div className="p-4 border-b grid grid-cols-5 gap-4 text-[12px] font-medium text-muted-foreground">
          <div>Mes</div>
          <div className="text-right">Saldo Obertura</div>
          <div className="text-right">Ingressos</div>
          <div className="text-right">Sortides</div>
          <div className="text-right">Saldo Tancament</div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin h-7 w-7 border-4 border-blue-500 border-t-transparent rounded-full mr-3" />
            <span className="text-gray-600">Carregant projeccions...</span>
          </div>
        )}

        {!loading && projections.length === 0 && !error && (
          <div className="p-8 text-center text-gray-500">
            No hi ha projeccions per a {year}. Afegeix elements de flux de caixa per generar-les.
          </div>
        )}

        {!loading && projections.map((proj) => {
          const incoming = Number(proj.total_incoming || 0);
          const outgoing = Number(proj.total_outgoing || 0);
          const opening  = Number(proj.opening_balance || 0);
          const closing  = Number(proj.closing_balance || 0);
          const isNegative = closing < 0;
          return (
            <div
              key={proj.month}
              className={`p-4 border-b grid grid-cols-5 gap-4 text-sm ${isNegative ? 'bg-red-50' : 'hover:bg-gray-50'}`}
            >
              <div className="font-medium">{MONTH_NAMES[proj.month - 1]}</div>
              <div className="text-right font-mono">
                {opening.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
              </div>
              <div className="text-right font-mono text-green-600">
                +{incoming.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
              </div>
              <div className="text-right font-mono text-red-600">
                -{outgoing.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
              </div>
              <div className={`text-right font-mono font-bold ${isNegative ? 'text-red-600' : 'text-blue-600'}`}>
                {closing.toLocaleString('ca-ES', { minimumFractionDigits: 2 })} €
                {isNegative && <span className="ml-1 text-xs text-red-500">⚠</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
